/**
 * A filter box with three states: unfiltered, has it, lacks it.
 *
 * WHY A THIRD STATE EXISTS
 *
 * The advanced-search boxes were plain checkboxes, so the only question they
 * could ask was "show me the rows WITH this". The question the operator
 * actually has is the other one -- which transactions will no rule code, which
 * have no receipt attached -- and a two-state box cannot ask it. Finding the
 * rows without a badge meant reading the whole list looking for absences,
 * which is the one thing a list is bad at.
 *
 * WHY NOT A NATIVE CHECKBOX WITH `indeterminate`
 *
 * It looks right and says the wrong thing. `indeterminate` renders as a dash
 * and is announced as "mixed", which means "some of the things below are
 * checked" -- a parent in a tree of checkboxes. It has no way at all to mean
 * "the negative of this", so a screen reader user would be told the filter was
 * partially applied when it was in fact inverted. There is no ARIA state for
 * a negated filter, so the state is written into the accessible NAME, where it
 * is unambiguous in every reader.
 *
 * WHY A CYCLE RATHER THAN THREE BUTTONS
 *
 * The operator asked for one control per filter, and these sit in a row that
 * also holds the search box. A segmented Any/Has/Lacks control is clearer in
 * isolation and three times as wide, and this row would wrap on a laptop.
 * The cost is that the third state is only discoverable by clicking, so the
 * glyph and the name both spell out what the current state means, and the
 * title says what the next click will do.
 */

export type TriState = 'any' | 'yes' | 'no';

/** Blank, tick, cross. The cycle the operator clicks through. */
const NEXT: Record<TriState, TriState> = { any: 'yes', yes: 'no', no: 'any' };

const GLYPH: Record<TriState, string> = { any: '', yes: '✓', no: '✕' };

export function TriStateCheck({
  label,
  value,
  onChange,
  /** What the two filtered states mean, in words, for the accessible name. */
  has,
  lacks,
}: {
  label: string;
  value: TriState;
  onChange: (next: TriState) => void;
  has: string;
  lacks: string;
}) {
  const said: Record<TriState, string> = {
    any: `${label}: not filtered`,
    yes: `${label}: ${has}`,
    no: `${label}: ${lacks}`,
  };
  const next = NEXT[value];

  return (
    <button
      type="button"
      className={`tri tri-${value}`}
      // The state is in the name because no ARIA state can carry "negated";
      // see the header. `title` is the NEXT state, so a click is predictable
      // without having to make it.
      aria-label={said[value]}
      title={`Click for ${said[next].toLowerCase()}`}
      onClick={() => onChange(next)}
    >
      <span className="tri-box" aria-hidden="true">
        {GLYPH[value]}
      </span>
      <span aria-hidden="true">{label}</span>
    </button>
  );
}
